/**
 * Webtech HR Engine™ - Subscription Management Page
 * Page de gestion de l'abonnement pour Admin RH
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Crown,
  Shield,
  Users,
  Calendar,
  CreditCard,
  Download,
  FileText,
  Settings,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  ExternalLink,
  RefreshCw,
  ArrowUpRight,
  Brain,
  Star,
  BarChart3,
  Lock,
  Unlock
} from 'lucide-react';

const MODULE_INFO = {
  peopleMapping: { name: 'People Mapping', icon: Users, description: 'Trombinoscope et organigramme' },
  performance: { name: 'Performance', icon: Star, description: 'Gestion des évaluations' },
  skills: { name: 'Compétences', icon: Brain, description: 'Gestion des compétences' },
  aiAnalysis: { name: 'Analyse IA', icon: Brain, description: 'Analyses intelligentes' },
  advancedReporting: { name: 'Reporting', icon: BarChart3, description: 'Rapports avancés' },
};

export default function SubscriptionPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [togglingModule, setTogglingModule] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [subRes, invRes] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/invoices')
      ]);
      
      setSubscription(subRes.data.subscription);
      setInvoices(invRes.data.invoices || []);
    } catch (error) {
      console.error('Error fetching subscription:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleModule = async (module, currentValue) => {
    if (subscription?.plan !== 'pro') {
      toast.error('Cette fonctionnalité n\'est disponible qu\'avec le plan PRO');
      return;
    }
    
    setTogglingModule(module);
    try {
      const response = await api.post('/billing/modules/toggle', {
        module,
        enabled: !currentValue
      });
      
      // Update local state
      setSubscription(prev => ({
        ...prev,
        activeModules: {
          ...prev.activeModules,
          [module]: !currentValue
        }
      }));
      
      // Déclencher un rafraîchissement global du contexte Plan
      window.dispatchEvent(new CustomEvent('plan-changed'));
      
      toast.success(`Module ${MODULE_INFO[module]?.name || module} ${!currentValue ? 'activé' : 'désactivé'}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setTogglingModule(null);
    }
  };

  const formatPrice = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      toast.loading('Téléchargement du PDF...', { id: 'pdf-download' });
      
      const response = await api.get(`/billing/invoices/${invoiceId}/pdf`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoiceNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Facture téléchargée', { id: 'pdf-download' });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Erreur lors du téléchargement', { id: 'pdf-download' });
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { label: 'Actif', class: 'bg-green-100 text-green-700' },
      suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-700' },
      grace_period: { label: 'Période de grâce', class: 'bg-amber-100 text-amber-700' },
      pending: { label: 'En attente', class: 'bg-blue-100 text-blue-700' },
      cancelled: { label: 'Annulé', class: 'bg-slate-100 text-slate-700' },
    };
    const c = config[status] || config.pending;
    return <Badge className={c.class}>{c.label}</Badge>;
  };

  const getInvoiceStatusBadge = (status) => {
    const config = {
      paid: { label: 'Payée', class: 'bg-green-100 text-green-700', icon: CheckCircle2 },
      pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700', icon: Clock },
      failed: { label: 'Échouée', class: 'bg-red-100 text-red-700', icon: AlertTriangle },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.class}>
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Mon abonnement">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  const isPro = subscription?.plan === 'pro';
  const isSuspended = subscription?.status === 'suspended';
  const isGracePeriod = subscription?.status === 'grace_period';

  return (
    <DashboardLayout title="Mon abonnement">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Alert for suspended or grace period */}
        {(isSuspended || isGracePeriod) && (
          <Card className={isSuspended ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`w-6 h-6 ${isSuspended ? 'text-red-600' : 'text-amber-600'}`} />
                <div>
                  <h4 className={`font-medium ${isSuspended ? 'text-red-800' : 'text-amber-800'}`}>
                    {isSuspended ? 'Abonnement suspendu' : 'Période de grâce'}
                  </h4>
                  <p className={`text-sm ${isSuspended ? 'text-red-600' : 'text-amber-600'}`}>
                    {isSuspended 
                      ? 'Votre abonnement a été suspendu. Veuillez régulariser votre paiement.'
                      : `Votre paiement a échoué. Vous avez jusqu'au ${formatDate(subscription.gracePeriodEndDate)} pour régulariser.`
                    }
                  </p>
                </div>
                <Button 
                  className={isSuspended ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
                  size="sm"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Régulariser
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscription overview */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  {isPro ? (
                    <><Crown className="w-6 h-6 text-amber-500" />Plan BUSINESS</>
                  ) : (
                    <><Shield className="w-6 h-6 text-slate-500" />Plan Gratuit</>
                  )}
                </CardTitle>
                <CardDescription>
                  {isPro 
                    ? 'Accès complet à tous les modules et fonctionnalités'
                    : 'Accès limité aux fonctionnalités de base'
                  }
                </CardDescription>
              </div>
              {getStatusBadge(subscription?.status)}
            </div>
          </CardHeader>
          <CardContent>
            {isPro ? (
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Users className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Utilisateurs</p>
                  <p className="text-2xl font-bold">{subscription.userCount || subscription.maxUsers}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <CreditCard className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Facturation</p>
                  <p className="text-2xl font-bold">
                    {subscription.frequency === 'yearly' ? 'Annuelle' : 'Mensuelle'}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Prochain paiement</p>
                  <p className="text-lg font-semibold">
                    {formatDate(subscription.nextBillingDate)}
                  </p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <FileText className="w-5 h-5 text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Montant</p>
                  <p className="text-2xl font-bold">
                    {formatPrice(subscription.totalAmount)} <span className="text-sm font-normal">FCFA</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Passez au plan PRO</h3>
                <p className="text-slate-500 mb-4 max-w-md mx-auto">
                  Débloquez tous les modules Performance, Compétences, l'Analyse IA et le Reporting avancé.
                </p>
                <Button 
                  className="bg-webtech-blue hover:bg-webtech-blue-hover"
                  onClick={() => navigate('/billing/pricing')}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Voir les tarifs
                </Button>
              </div>
            )}
          </CardContent>
          {isPro && (
            <CardFooter className="border-t bg-slate-50">
              <div className="flex items-center justify-between w-full">
                <p className="text-sm text-slate-500">
                  Abonnement depuis le {formatDate(subscription.startDate)}
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate('/billing/pricing')}>
                  <Settings className="w-4 h-4 mr-2" />
                  Modifier l'abonnement
                </Button>
              </div>
            </CardFooter>
          )}
        </Card>

        {/* Module management (PRO only) */}
        {isPro && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Gestion des modules
              </CardTitle>
              <CardDescription>
                Activez ou désactivez les modules selon vos besoins
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(MODULE_INFO).map(([key, info]) => {
                  const isEnabled = subscription.activeModules?.[key];
                  const Icon = info.icon;
                  const isToggling = togglingModule === key;
                  
                  // People Mapping is always enabled
                  const isRequired = key === 'peopleMapping';
                  
                  return (
                    <div key={key} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isEnabled ? 'bg-webtech-blue/10 text-webtech-blue' : 'bg-slate-100 text-slate-400'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium">{info.name}</h4>
                          <p className="text-sm text-slate-500">{info.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {isRequired ? (
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <Lock className="w-3 h-3 mr-1" />
                            Toujours actif
                          </Badge>
                        ) : (
                          <>
                            <Label className="text-sm text-slate-500">
                              {isEnabled ? 'Activé' : 'Désactivé'}
                            </Label>
                            <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleToggleModule(key, isEnabled)}
                              disabled={isToggling || isSuspended}
                            />
                            {isToggling && <Loader2 className="w-4 h-4 animate-spin" />}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Invoices */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Factures
            </CardTitle>
            <CardDescription>
              Historique de vos factures et paiements
            </CardDescription>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                Aucune facture pour le moment
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>N° Facture</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Période</TableHead>
                    <TableHead>Montant</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                      <TableCell>{formatDate(invoice.createdAt)}</TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                      </TableCell>
                      <TableCell>{formatPrice(invoice.totalAmount)} FCFA</TableCell>
                      <TableCell>{getInvoiceStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDownloadPdf(invoice.id, invoice.invoiceNumber)}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          PDF
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
